import type { ToolContent } from "./tool-content";
import { getFrPdfToolBySlug } from "./fr-pdf-catalog";

interface FrPdfSeoOverride {
  h1Title: string;
  seoTitle: string;
  seoDescription: string;
  intro: string;
  faq?: { q: string; a: string }[];
}

const FR_PDF_SEO_DATA: Record<string, FrPdfSeoOverride> = {
  "fusionner-pdf": {
    h1Title: "Fusionner des PDF en Ligne Gratuitement — Assembler vos Fichiers PDF",
    seoTitle: "Fusionner des PDF en Ligne Gratuitement | Picamini.online",
    seoDescription: "Fusionnez et assemblez plusieurs fichiers PDF en un seul document clair en ligne. 100% gratuit, aucun téléversement sur serveur, confidentialité garantie.",
    intro: "Combinez deux ou plusieurs documents PDF en un fichier unique et parfaitement ordonné. Réorganisez l'ordre des pages et téléchargez votre résultat en quelques secondes en toute confidentialité.",
    faq: [
      {
        q: "Est-ce sécurisé de fusionner mes PDF sur Picamini.online ?",
        a: "Absolument. La fusion est effectuée à 100% localement dans votre propre navigateur grâce au traitement WebAssembly/JavaScript. Vos documents confidentiels ne quittent jamais votre appareil."
      },
      {
        q: "Y a-t-il une limite au nombre de fichiers PDF à fusionner ?",
        a: "Non, il n'y a aucune limite artificielle. Vous pouvez assembler autant de fichiers PDF que la mémoire vive de votre appareil le permet."
      }
    ]
  },
  "diviser-pdf": {
    h1Title: "Diviser un PDF & Séparer les Pages en Ligne Gratuitement",
    seoTitle: "Diviser un PDF Gratuitement en Ligne — Séparer les Pages | Picamini.online",
    seoDescription: "Divisez votre PDF gratuitement en ligne — extayez des pages individuelles ou séparez des plages de pages en fichiers distincts sans téléversement serveur.",
    intro: "Découpez vos documents PDF ou extayez des pages spécifiques dans de nouveaux fichiers PDF — rapide, sécurisé et totalement confidentiel.",
    faq: [
      {
        q: "Puis-je enregistrer seulement une page précise d'un PDF ?",
        a: "Oui, vous pouvez spécifier les numéros de page exacts ou sélectionner visuellement les pages à exporter dans un nouveau fichier PDF."
      }
    ]
  },
  "organiser-pdf": {
    h1Title: "Organiser & Réorganiser les Pages d'un PDF Gratuitement",
    seoTitle: "Organiser les Pages d'un PDF Gratuitement en Ligne | Picamini.online",
    seoDescription: "Réorganisez les pages d'un PDF par glisser-déplacer, supprimez les feuillets inutiles ou pivotez les pages dans votre navigateur.",
    intro: "Restructurez vos fichiers PDF : réordonnez les pages à la souris, retirez les pages blanches et corrigez l'orientation des feuillets.",
  },
  "pivoter-pdf": {
    h1Title: "Pivoter un PDF en Ligne Gratuitement — Orienter vos Pages",
    seoTitle: "Pivoter un PDF en Ligne Gratuitement | Picamini.online",
    seoDescription: "Pivotez des pages individuelles ou l'ensemble d'un document PDF à 90°, 180° ou 270° directement dans votre navigateur.",
    intro: "Corrigez de façon permanente l'orientation des pages PDF scannées à l'envers ou mal positionnées et téléchargez un document impeccable.",
  },
  "extraire-pages-pdf": {
    h1Title: "Extraire des Pages d'un PDF Gratuitement — Sauvegarder des Pages",
    seoTitle: "Extraire des Pages d'un PDF en Ligne Gratuitement | Picamini.online",
    seoDescription: "Extrayez des pages ciblées d'un document PDF volumineux et générez un nouveau fichier PDF léger sans téléversement serveur.",
    intro: "Sélectionnez précisément les pages dont vous avez besoin et créez en un clic un nouveau document PDF prêt à être partagé.",
  },
  "supprimer-pages-pdf": {
    h1Title: "Supprimer des Pages d'un PDF Gratuitement — Retirer des Feuillets",
    seoTitle: "Supprimer des Pages d'un PDF en Ligne Gratuitement | Picamini.online",
    seoDescription: "Effacez définitivement les pages inutiles ou vierges de vos fichiers PDF directement dans votre navigateur Web.",
    intro: "Retirez rapidement les pages en trop ou périmées de vos documents PDF sans altérer le reste de votre fichier.",
  },
  "modifier-pdf": {
    h1Title: "Modifier un PDF en Ligne Gratuitement — Éditeur PDF Complet",
    seoTitle: "Modifier un PDF en Ligne Gratuitement — Éditeur PDF | Picamini.online",
    seoDescription: "Éditez vos fichiers PDF gratuitement : ajoutez du texte, des formes, des dessins libres, des signatures et des images directement dans le navigateur.",
    intro: "Complétez vos formulaires PDF, annotez vos documents, insérez des zones de texte et apposez votre signature numérique en toute sécurité.",
    faq: [
      {
        q: "Puis-je ajouter du texte et signer mon document PDF ?",
        a: "Oui ! Notre éditeur PDF vous permet de saisir du texte, d'ajouter des formes, de dessiner à la main et d'insérer une signature manuscrite."
      }
    ]
  },
  "numeroter-pages-pdf": {
    h1Title: "Ajouter des Numéros de Page dans un PDF Gratuitement",
    seoTitle: "Ajouter des Numéros de Page dans un PDF en Ligne | Picamini.online",
    seoDescription: "Numérotez les pages de vos documents PDF facilement : choisissez l'emplacement (en-tête, pied de page), la taille et la police.",
    intro: "Ajoutez une numérotation professionnelle sur l'ensemble de vos documents PDF avec une personnalisation complète du style et de l'alignement.",
  },
  "filigrane-pdf": {
    h1Title: "Ajouter un Filigrane dans un PDF — Marquer vos Documents",
    seoTitle: "Ajouter un Filigrane dans un PDF en Ligne Gratuitement | Picamini.online",
    seoDescription: "Protégez vos documents confidentiels en ajoutant un filigrane texte ou un logo personnalisé sur vos pages PDF.",
    intro: "Apposez des filigranes 'Confidentiel', 'Draft' ou le logo de votre entreprise sur toutes les pages de vos fichiers PDF.",
  },
  "compresser-pdf": {
    h1Title: "Compresser un PDF & Réduire sa Taille Gratuitement",
    seoTitle: "Compresser un PDF en Ligne Gratuitement — Réduire le Poids | Picamini.online",
    seoDescription: "Comprimez vos documents PDF trop lourds sans perte de qualité visuelle. Idéal pour les envois d'e-mails et formulaires web.",
    intro: "Réduisez significativement la taille de vos fichiers PDF pour faciliter leur envoi par courriel et respecter les contraintes de téléversement.",
    faq: [
      {
        q: "La qualité du texte et des images sera-t-elle altérée ?",
        a: "L'outil optimise la compression pour réduire le poids du fichier tout en maintenant une excellente lisibilité du texte et des illustrations."
      }
    ]
  },
  "pdf-noir-et-blanc": {
    h1Title: "Convertir un PDF en Noir et Blanc (Niveaux de Gris)",
    seoTitle: "Convertir un PDF en Noir et Blanc en Ligne | Picamini.online",
    seoDescription: "Transformez des documents PDF couleur en niveaux de gris pour économiser l'encre d'impression et réduire le poids du fichier.",
    intro: "Convertissez en un instant vos PDF couleur en noir et blanc afin d'optimiser l'impression papier et faire des économies d'encre.",
  },
  "reparer-pdf": {
    h1Title: "Réparer un PDF Endommagé en Ligne Gratuitement",
    seoTitle: "Réparer un PDF Endommagé en Ligne Gratuitement | Picamini.online",
    seoDescription: "Restaurez la structure et les données de vos fichiers PDF corrompus ou illisibles directement dans votre navigateur.",
    intro: "Tentez d'extraire et de reconstruire les pages lisibles d'un fichier PDF endommagé pour récupérer votre travail précieux.",
  },
  "image-en-pdf": {
    h1Title: "Convertir Image en PDF — JPG, PNG, WEBP en PDF",
    seoTitle: "Convertir Image en PDF Gratuitement (JPG, PNG) | Picamini.online",
    seoDescription: "Convertissez facilement vos photos et images JPG, PNG, WEBP en un document PDF mis en page proprement.",
    intro: "Transformez vos fichiers d'images ou photos scannées en un document PDF structuré. Combinez plusieurs photos en un seul fichier en un instant.",
  },
  "word-en-pdf": {
    h1Title: "Convertir Word en PDF (DOCX en PDF) en Ligne",
    seoTitle: "Convertir Word en PDF Gratuitement en Ligne | Picamini.online",
    seoDescription: "Convertissez vos fichiers Word (.docx, .doc) en documents PDF professionnels dans votre navigateur sans altérer la mise en page.",
    intro: "Exportez vos documents Word au format PDF pour garantir une lecture identique sur tous les ordinateurs et téléphones.",
  },
  "excel-en-pdf": {
    h1Title: "Convertir Excel en PDF (XLSX en PDF) en Ligne",
    seoTitle: "Convertir Excel en PDF en Ligne Gratuitement | Picamini.online",
    seoDescription: "Convertissez vos feuilles de calcul et classeurs Excel (.xlsx) en fichiers PDF clairs et prêts à l'impression.",
    intro: "Transformez vos tableaux financiers et feuilles de données Excel en documents PDF faciles à consulter et à imprimer.",
  },
  "powerpoint-en-pdf": {
    h1Title: "Convertir PowerPoint en PDF (PPTX en PDF) en Ligne",
    seoTitle: "Convertir PowerPoint en PDF Gratuitement en Ligne | Picamini.online",
    seoDescription: "Convertissez vos présentations PPTX et PPT en documents PDF légers et faciles à distribuer.",
    intro: "Préservez le rendu de vos diapositives PowerPoint en les convertissant en un fichier PDF universel.",
  },
  "html-en-pdf": {
    h1Title: "Convertir HTML en PDF — Enregistrer Page Web en PDF",
    seoTitle: "Convertir HTML en PDF en Ligne Gratuitement | Picamini.online",
    seoDescription: "Convertissez du code HTML ou des fichiers web en documents PDF imprimables directement dans votre navigateur.",
    intro: "Transformez vos fichiers de code HTML en documents PDF mis en forme proprement.",
  },
  "texte-en-pdf": {
    h1Title: "Convertir Fichier Texte TXT en PDF en Ligne",
    seoTitle: "Convertir TXT en PDF en Ligne Gratuitement | Picamini.online",
    seoDescription: "Convertissez vos notes et fichiers texte brut (.txt) en documents PDF élégants avec mise en forme sur mesure.",
    intro: "Transformez vos fichiers texte brut en documents PDF lisibles avec police et marges personnalisables.",
  },
  "pdf-en-image": {
    h1Title: "Convertir PDF en Image — Exporter PDF en JPG / PNG",
    seoTitle: "Convertir PDF en Image (JPG, PNG) Gratuitement | Picamini.online",
    seoDescription: "Extrayez les pages de vos fichiers PDF sous forme d'images haute résolution JPG ou PNG en ligne.",
    intro: "Transformez chaque page de votre fichier PDF en image individuelle de haute qualité prête à être réutilisée.",
  },
  "pdf-en-word": {
    h1Title: "Convertir PDF en Word (DOCX) — Modifier le Texte PDF",
    seoTitle: "Convertir PDF en Word Modifiable en Ligne Gratuitement | Picamini.online",
    seoDescription: "Extrayez le texte de vos PDF et convertissez-les en fichiers Word (.docx) rééditables directement dans votre navigateur.",
    intro: "Récupérez le contenu textuel de vos fichiers PDF dans un document Word rééditable sans perdre votre travail.",
  },
  "pdf-en-excel": {
    h1Title: "Convertir PDF en Excel (XLSX) — Extraire les Tableaux",
    seoTitle: "Convertir PDF en Excel en Ligne Gratuitement | Picamini.online",
    seoDescription: "Extrayez les tableaux et données chiffrées de vos PDF vers des feuilles de calcul Excel (.xlsx) modifiables.",
    intro: "Convertissez les tableaux de vos rapports PDF en véritables feuilles de calcul Excel prêtes à être analysées.",
  },
  "extraire-texte-pdf": {
    h1Title: "Extraire le Texte d'un PDF en Fichier TXT",
    seoTitle: "Extraire le Texte d'un PDF en Ligne Gratuitement | Picamini.online",
    seoDescription: "Récupérez tout le texte brut d'un document PDF dans un fichier texte léger (.txt) en un clic.",
    intro: "Copiez et extrayez rapidement le contenu écrit d'un fichier PDF pour le réutiliser dans vos notes ou présentations.",
  },
  "proteger-pdf": {
    h1Title: "Protéger un PDF par Mot de Passe — Chiffrer votre Fichier",
    seoTitle: "Protéger un PDF par Mot de Passe en Ligne | Picamini.online",
    seoDescription: "Sécurisez et chiffrez vos documents PDF confidentiels avec un mot de passe d'accès fort et privé.",
    intro: "Ajoutez un mot de passe et un chiffrement robuste sur vos fichiers PDF sensibles pour empêcher les accès non autorisés.",
  },
  "deverrouiller-pdf": {
    h1Title: "Déverrouiller un PDF — Supprimer le Mot de Passe",
    seoTitle: "Déverrouiller un PDF en Ligne Gratuitement | Picamini.online",
    seoDescription: "Retirez la protection par mot de passe et les restrictions d'impression de vos propres documents PDF.",
    intro: "Déverrouillez vos fichiers PDF protégés par mot de passe dont vous êtes le propriétaire légitime.",
  },
  "signer-pdf": {
    h1Title: "Signer un PDF en Ligne — Signature Électronique Gratuit",
    seoTitle: "Signer un PDF en Ligne Gratuitement | Picamini.online",
    seoDescription: "Signez vos contrats, devis et formulaires PDF en ligne : dessinez votre signature ou téléversez une image de signature.",
    intro: "Apposez facilement votre signature manuscrite ou numérique sur n'importe quel fichier PDF directement dans votre navigateur.",
  },
  "ocr-pdf": {
    h1Title: "Reconnaissance de Texte OCR PDF — Rendre le Scan Chercheable",
    seoTitle: "OCR PDF Gratuit en Ligne — Reconnaissance de Texte | Picamini.online",
    seoDescription: "Appliquez l'OCR sur vos documents PDF scannés pour rendre le texte sélectionnable et recherchable.",
    intro: "Numérisez et convertissez vos documents scannés en texte recherchable grâce à la technologie OCR intégrée.",
  },
};

export function getFrPdfToolContent(slug: string, fallbackName?: string): ToolContent {
  const meta = getFrPdfToolBySlug(slug);
  const frSlug = meta ? meta.slug : slug;
  const enSlug = meta ? meta.enSlug : slug;
  const override = FR_PDF_SEO_DATA[frSlug] || FR_PDF_SEO_DATA[enSlug];

  const name = fallbackName || meta?.name || slug.replace(/-/g, " ");
  const tagline = meta?.tagline || "Outil PDF professionnel gratuit en ligne";
  const desc = meta?.description || "Traitez vos documents PDF directement dans votre navigateur en toute confidentialité.";

  const h1Title = override?.h1Title || `${name} — En Ligne & Gratuit`;
  const seoTitle = override?.seoTitle || `${name} | Picamini.online`;
  const seoDescription = override?.seoDescription || `${desc} 100% gratuit et privé sans téléversement sur serveur.`;
  const intro = override?.intro || `${tagline}. ${desc} Aucune inscription n'est requise et vos fichiers restent exclusivement sur votre appareil.`;

  return {
    seoTitle,
    seoDescription,
    h1Title,
    intro,
    whatIs: [
      `L'outil ${name} de Picamini.online fait partie de notre suite complète PDF Toolkit Pro. Il est spécialement conçu pour résoudre vos besoins de gestion documentaire au quotidien.`,
      "À la différence des convertisseurs PDF traditionnels qui envoient vos fichiers confidentiels sur des serveurs distants, notre technologie s'exécute entièrement dans la mémoire locale de votre navigateur Web via JavaScript et WebAssembly.",
      "Cela garantit une sécurité optimale, une rapidité de traitement inégalée et le respect total du Règlement Général sur la Protection des Données (RGPD)."
    ],
    howTo: [
      `Accédez à l'outil ${name} dans votre navigateur.`,
      "Faites glisser votre fichier PDF ou sélectionnez-le depuis votre ordinateur, tablette ou mobile.",
      "Ajustez les options et paramètres selon vos besoins.",
      "Cliquez sur le bouton de traitement pour appliquer les modifications.",
      "Téléchargez immédiatement votre fichier finalisé sur votre appareil."
    ],
    features: [
      "Traitement 100% local — Vos fichiers ne quittent jamais votre ordinateur",
      "Totalement gratuit sans filigrane forcé ni limitation d'utilisation",
      "Aucune création de compte ni installation de logiciel requise",
      "Compatibilité totale avec tous les navigateurs récents (Chrome, Safari, Firefox, Edge)",
      "Interface simple, moderne et adaptée aux smartphones et tablettes"
    ],
    benefits: [
      "Confidentialité maximale pour vos documents professionnels, médicaux ou administratifs",
      "Exécution ultra rapide indépendante de la vitesse de votre connexion montante",
      "Zéro coût : économisez sur les abonnements logiciels coûteux",
      "Respect strict des normes européennes de protection des données"
    ],
    proTips: [
      "Pour des résultats optimaux avec de gros fichiers, fermez les onglets inutiles de votre navigateur afin de libérer de la mémoire RAM.",
      "Ajoutez cet outil à vos favoris pour y accéder en un clic lors de vos travaux administratifs."
    ],
    useCases: [
      {
        role: "Entreprises & Professionnels",
        description: "Préparez des factures, contrats, devis et rapports officiels en toute confidentialité."
      },
      {
        role: "Étudiants & Enseignants",
        description: "Assemblez des cours, numérotez des mémoires de recherche ou compressez des exposés pour l'envoi par e-mail."
      }
    ],
    detailedGuide: [
      {
        heading: `Comment fonctionne l'outil ${name} dans le navigateur ?`,
        paragraphs: [
          `Traditionnellement, la modification de fichiers PDF nécessitait soit des logiciels payants très lourds, soit le transfert de vos documents sur des serveurs tiers anonymes.`,
          `Grâce aux avancées récentes des navigateurs Web modernes, Picamini.online exécute l'intégralité du traitement de vos documents PDF directement au sein de votre session navigateur. Vos données personnelles restent sous votre contrôle exclusif.`
        ],
        bulletPoints: [
          "Confidentialité absolue garantie",
          "Traitement instantané sans temps d'envoi",
          "Fonctionnement hors-ligne possible une fois la page chargée"
        ]
      }
    ],
    faq: override?.faq || [
      {
        q: `L'outil ${name} est-il vraiment gratuit ?`,
        a: "Oui, tous les outils de la suite PDF Toolkit Pro de Picamini.online sont 100% gratuits et sans restriction."
      },
      {
        q: "Mes fichiers sont-ils stockés sur vos serveurs ?",
        a: "Non, absolument aucun fichier n'est téléversé sur nos serveurs. L'ensemble des opérations s'effectue dans votre navigateur."
      }
    ]
  };
}
